import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Copy, CopyIcon } from "lucide-react";
import { getPromptBySlug, getAllPrompts } from "@/lib/prompts";
import { CuratedPromptCard } from "@/components/curated-prompt-card";
import { toast } from "sonner";

export const Route = createFileRoute("/prompts/$slug")({
  loader: async ({ params }) => {
    const prompt = await getPromptBySlug({ data: params.slug });

    if (!prompt) {
      throw notFound();
    }

    // Fetch related prompts (same category, excluding current prompt)
    const allPrompts = await getAllPrompts({ data: undefined });
    const relatedPrompts = allPrompts
      .filter(p => p.category === prompt.category && p.slug !== prompt.slug)
      .slice(0, 4);

    return { prompt, relatedPrompts };
  },

  // Dynamic SEO meta tags per prompt
  head: ({ loaderData }) => {
    const prompt = loaderData?.prompt;
    const pageTitle =
      prompt?.metaTitle ||
      (prompt?.title
        ? `${prompt.title} - AI Prompt Example | Images AI`
        : "AI Prompt Example | Images AI");
    const pageDescription =
      prompt?.metaDescription ||
      (prompt?.prompt
        ? `AI image generation prompt: "${prompt.prompt}". Professional example for creating ${prompt.category || "stunning"} images with AI.`
        : "Explore AI image generation prompts");

    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: pageDescription },
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: pageDescription },
        { property: "og:type", content: "article" },
        ...(prompt?.slug
          ? [
              {
                property: "og:url",
                content: `https://images-ai.app/prompts/${prompt.slug}`,
              },
            ]
          : []),
        ...(prompt?.imageUrl
          ? [{ property: "og:image", content: prompt.imageUrl }]
          : []),
        { property: "twitter:card", content: "summary_large_image" },
        { property: "twitter:title", content: pageTitle },
        { property: "twitter:description", content: pageDescription },
        ...(prompt?.imageUrl
          ? [{ property: "twitter:image", content: prompt.imageUrl }]
          : []),
      ],
      links: prompt?.slug
        ? [
            {
              rel: "canonical",
              href: `https://images-ai.app/prompts/${prompt.slug}`,
            },
          ]
        : [],
      // JSON-LD ImageObject and BreadcrumbList structured data
      scripts: prompt
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ImageObject",
                name: prompt.title,
                description: prompt.prompt,
                contentUrl: prompt.imageUrl,
                url: `https://images-ai.app/prompts/${prompt.slug}`,
                datePublished: prompt.createdAt.toISOString(),
                thumbnailUrl: prompt.imageUrl,
                encodingFormat: "image/png",
                creator: {
                  "@type": "Organization",
                  name: "Images AI",
                  url: "https://images-ai.app",
                },
                copyrightHolder: {
                  "@type": "Organization",
                  name: "Images AI",
                },
                keywords: prompt.tags.join(", "),
                about: prompt.category,
                isAccessibleForFree: true,
                license: "https://images-ai.app/terms",
                copyrightNotice: "Images AI",
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    item: {
                      "@id": "https://images-ai.app/",
                      name: "Home",
                    },
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    item: {
                      "@id": "https://images-ai.app/prompts",
                      name: "Prompts",
                    },
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    item: {
                      "@id": `https://images-ai.app/prompts/${prompt.slug}`,
                      name: prompt.title,
                    },
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },

  component: PromptDetailPage,
});

function PromptDetailPage() {
  const { prompt, relatedPrompts } = Route.useLoaderData();
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(prompt.prompt);
    toast.success("Prompt copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        {/* Back button */}
        <div className="mb-6">
          <Link to="/prompts">
            <Button variant="ghost" size="sm" className="rounded-full">
              ← Back to Gallery
            </Button>
          </Link>
        </div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* Image Section */}
          <div className="space-y-6">
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl">
              <img
                src={prompt.imageUrl}
                alt={`${prompt.title} - AI generated ${prompt.category || "image"} example`}
                title={`Example result from prompt: ${prompt.prompt.slice(0, 80)}${prompt.prompt.length > 80 ? "..." : ""}`}
                className="w-full h-auto"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            {/* Metadata */}
            {prompt.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {prompt.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-muted text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold mb-4">{prompt.title}</h1>
              {prompt.category && (
                <p className="text-sm text-muted-foreground uppercase tracking-wide">
                  {prompt.category}
                </p>
              )}
            </div>

            {/* Prompt Display */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Prompt</h2>
              <div className="relative">
                <div className="p-6 rounded-2xl bg-muted border border-border">
                  <p className="text-sm leading-relaxed font-mono">
                    {prompt.prompt}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="absolute bottom-2 right-2 rounded-xl bg-transparent hover:bg-white"
                  onClick={handleCopyPrompt}
                >
                  <CopyIcon className="w-2 h-2" /> Copy
                </Button>
              </div>
            </div>

            {/* Model Info */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Model Used
              </h3>
              <p className="text-sm font-mono">{prompt.modelId}</p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3 pt-6">
              {session ? (
                <Button
                  size="lg"
                  className="w-full rounded-full"
                  onClick={() => { handleCopyPrompt(); navigate({ to: "/dashboard" }); }}
                >
                  Copy & Use This Prompt
                </Button>
              ) : (
                <>
                  <Link to="/signup" className="block">
                    <Button size="lg" className="w-full rounded-full">
                      Try This Prompt - Sign Up Free
                    </Button>
                  </Link>
                  <Link to="/prompts" className="block">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full rounded-full"
                    >
                      Explore More Prompts
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Related Prompts Section */}
        {relatedPrompts.length > 0 && (
          <section className="mt-16 border-t border-border pt-16">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-bold mb-8">
                More {prompt.category ? `${prompt.category.charAt(0).toUpperCase() + prompt.category.slice(1)}` : ''} Prompts
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedPrompts.map((relatedPrompt, index) => (
                  <CuratedPromptCard key={relatedPrompt.slug} prompt={relatedPrompt} index={index} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
